import {
  Accordion,
  AccordionSizeEnum,
  Badge,
  BadgeVariantEnum,
  BrandIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  TagsIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Listbox from '@/components/Listbox';
import { useGetLabels } from '@/features/labels/api/get-labels';
import { useLogos } from '@/features/logos/api/get-logos';
import { LogoCatalogueListbox } from '@/features/logos/components/LogoCatalogueListbox';
import { useLogosLabelsFilterStore } from '@/features/logos/stores/logos-labels-filter';
import { TLogo } from '@/features/logos/types';
import { TFilterProps } from '@/types';

import { TActiveOptions } from '../active-options/active-options';
import {
  BroadcastCatalogueActiveOptions,
  TBroadcastCatalogueRequest,
} from '../active-options/BroadcastCatalogueActiveOptions';
import { TUseCatalogueOptions } from '../active-options/CatalogueActiveOptions';

const toItem = (logo: TLogo) => ({
  value: logo.id,
  image: logo.parameters?.main_logo_presigned_url,
  label: logo.name,
});

/** The label narrowing applies to the active ids only: a selected logo is named whatever it. */
const useLogoOptions: TUseCatalogueOptions<TLogo, TBroadcastCatalogueRequest> = (request, config) =>
  useLogos({
    params:
      'active' in request
        ? {
            idIn: request.active,
            labelsIn: request.narrowing?.labelsIn?.length ? request.narrowing.labelsIn : undefined,
            limit: request.active.length,
          }
        : { idIn: request.idIn, limit: request.idIn.length },
    config,
  });

type MentionFilterSegmentLogoProps = TFilterProps & {
  value: string[];
  onChange: (next: string[]) => void;
  showLabelFilter?: boolean;
  activeOptions?: TActiveOptions;
};

export function MentionFilterSegmentLogo({
  isOpen,
  isDark,
  showLabel,
  onToggleFilter,
  onClose,
  className = '',
  value,
  onChange,
  showLabelFilter = false,
  activeOptions,
}: MentionFilterSegmentLogoProps) {
  const { t } = useTranslation();
  const { data: labelsData } = useGetLabels(undefined, { enabled: showLabelFilter });
  const selectedLabels = useLogosLabelsFilterStore(state => state.labels);
  const setSelectedLabels = useLogosLabelsFilterStore(state => state.setLabels);
  const [isLabelFilterOpen, setIsLabelFilterOpen] = useState(false);

  // The store selection is shared with other logo UIs and persisted to localStorage, so a
  // view that does not offer the sub-filter must not inherit one: it would narrow the list
  // by a label the operator cannot see selected, let alone clear.
  const labelsFilter = showLabelFilter ? selectedLabels : [];
  const narrowing = useMemo(() => ({ labelsIn: labelsFilter }), [labelsFilter]);

  const labelItems = useMemo(
    () =>
      (labelsData ?? [])
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ id, name }) => ({ value: id, label: name })),
    [labelsData]
  );

  const ToolTipWrapper = ({ children }: { children: React.ReactNode }) => {
    if (showLabel) return <>{children}</>;
    return (
      <Tooltip help={t('Logotipos')} grow>
        {children}
      </Tooltip>
    );
  };

  return (
    <div className="relative w-full md:w-auto">
      <div
        onClick={onToggleFilter}
        className={clsx(
          'group flex w-full cursor-pointer items-center border-b-[1px] border-stroke-30-1 p-1',
          { 'border-text-medium-20': isOpen && !isDark },
          { 'hover:border-text-medium-20': !isDark },
          { 'border-text-neutral-10 hover:bg-reverse-opacity-05/10': isDark },
          { 'bg-reverse-opacity-05/10': isOpen && isDark },
          className
        )}
      >
        <ToolTipWrapper>
          <BrandIcon
            className={clsx(
              'mr-1',
              {
                'text-text-medium-10': !isOpen && !isDark,
                'text-accent-active-primary-40': isOpen && !isDark,
              },
              {
                'text-text-neutral-10': isDark,
              },
              {
                'group-hover:text-accent-active-primary-40': !isDark,
              }
            )}
          />
        </ToolTipWrapper>
        {showLabel && (
          <Typography
            fontSize={FontSizeEnum.Sm}
            weight={FontWeightEnum.Medium}
            color={isDark ? FontColorEnum.Neutral : FontColorEnum.Medium}
            className={clsx(
              'select-none pr-size-4',
              {
                'text-text-medium-20': isOpen && !isDark,
              },
              {
                'group-hover:text-text-medium-20': !isDark,
              }
            )}
          >
            {t('Logotipos')}
          </Typography>
        )}
        {value.length > 0 && (
          <Badge
            variant={!isDark ? BadgeVariantEnum.DefaultFilled : BadgeVariantEnum.NeutralFilled}
            className="py-3"
          >
            + {value.length}
          </Badge>
        )}
      </div>
      {isOpen && (
        <div className="left-0 top-[120%] z-50 w-full bg-neutral-10 p-3 md:absolute md:min-w-[420px] md:shadow-full-medium">
          {showLabelFilter && labelItems.length > 0 && (
            <Accordion
              title={t('Filtro por etiquetas')}
              icon={TagsIcon}
              isOpen={isLabelFilterOpen}
              onOpen={() => setIsLabelFilterOpen(prev => !prev)}
              size={AccordionSizeEnum.Xs}
            >
              <Listbox
                dark={isDark}
                mode="multiple"
                items={labelItems}
                onChange={setSelectedLabels}
                identifiers={{ key: 'value', value: 'value', label: 'label' }}
                selected={selectedLabels}
                noApplies
                noSave
              />
            </Accordion>
          )}
          {activeOptions?.source === 'programBroadcast' ? (
            <BroadcastCatalogueActiveOptions
              activeOptions={activeOptions}
              useOptions={useLogoOptions}
              toItem={toItem}
              dimension="logos"
              narrowing={narrowing}
              isDark={isDark}
              selected={value}
              onChange={onChange}
              onClose={onClose}
            />
          ) : (
            <LogoCatalogueListbox
              mode="multiple"
              isDark={isDark}
              value={value}
              onChange={onChange}
              onClose={onClose}
              labelsFilter={labelsFilter}
              activeOptions={activeOptions}
            />
          )}
        </div>
      )}
    </div>
  );
}
